import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from './supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessionExpiresAt: Date | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, metadata?: Record<string, any>) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: { 
    full_name?: string; 
    avatar_url?: string; 
    phone?: string;
    company?: string;
    timezone?: string;
    notifications?: any;
    security?: any;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);

  const setSessionExpiration = (loginTime: Date) => {
    const expirationTime = new Date(loginTime.getTime() + 2 * 60 * 60 * 1000); // 2 horas
    setSessionExpiresAt(expirationTime);
    localStorage.setItem('sessionLoginTime', loginTime.toISOString());
  };

  const clearSessionExpiration = () => {
    setSessionExpiresAt(null);
    localStorage.removeItem('sessionLoginTime');
  };

  const checkSessionExpiration = () => {
    const loginTimeStr = localStorage.getItem('sessionLoginTime');
    if (!loginTimeStr || !user) return;

    const loginTime = new Date(loginTimeStr);
    const now = new Date();
    const twoHours = 2 * 60 * 60 * 1000;

    if (now.getTime() - loginTime.getTime() >= twoHours) {
      logout();
    } else {
      setSessionExpiration(loginTime);
    }
  };

  useEffect(() => {
    // Always use real Supabase authentication
    console.log('🔍 Using real Supabase authentication');

    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        if (session?.user) {
          checkSessionExpiration();
        }
      } catch (error) {
        console.warn('Supabase auth error:', error);
        setUser(null);
      }
      setLoading(false);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          setSessionExpiration(new Date());
          Sentry.setUser({
            id: session.user.id,
            email: session.user.email,
            username: session.user.user_metadata?.full_name,
          });
        } else {
          clearSessionExpiration();
          Sentry.setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !sessionExpiresAt) return;

    const interval = setInterval(() => {
      const now = new Date();
      if (now >= sessionExpiresAt) {
        logout();
      }
    }, 60000); // Verificar cada minuto

    return () => clearInterval(interval);
  }, [user, sessionExpiresAt]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const register = async (email: string, password: string, metadata?: Record<string, any>) => {
    const refToken = (typeof window !== 'undefined' ? localStorage.getItem('ref_token') : null) || metadata?.ref_token
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          ...(metadata || {}),
          ...(refToken ? { ref_token: refToken } : {}),
        }
      }
    });
    if (error) throw error;
  };

  const logout = async () => {
    clearSessionExpiration();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const updateProfile = async (updates: { 
    full_name?: string; 
    avatar_url?: string; 
    phone?: string;
    company?: string;
    timezone?: string;
    notifications?: any;
    security?: any;
  }) => {
    const { error } = await supabase.auth.updateUser({
      data: updates
    });
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    sessionExpiresAt,
    login,
    register,
    logout,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};